import React, { FC, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Tooltip,
  Skeleton,
  Typography,
  Chip,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Divider,
  Stack,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  Group as GroupIcon,
  EventSeat as EventSeatIcon,
  Checklist as ChecklistIcon,
} from "@mui/icons-material";
import { fetchBoards, getBoards } from "../../features/tournoi/tournoiSlice";
import { useAppDispatch, useAppSelector } from "../../store";
import { Tableau } from "../../types";

interface BoardListProps {}

const BoardList: FC<BoardListProps> = () => {
  const boards = useAppSelector(getBoards);
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [maxHeight, setMaxHeight] = useState<number>(600);

  useEffect(() => {
    dispatch(fetchBoards());
  }, [dispatch]);

  useEffect(() => {
    const updateHeight = () => {
      const appBar = document.querySelector("nav.MuiAppBar-root");
      const appBarHeight = appBar?.getBoundingClientRect().height || 64;
      const padding = 44;
      setMaxHeight(window.innerHeight - appBarHeight - padding);
    };
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  if (!boards || boards.length === 0) {
    return <TableRowsLoader rowsNum={6} />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        px: isMobile ? 1 : 0,
        ...(isMobile && {
          flexGrow: 1,
          minHeight: "auto",
          height: "auto",
          overflow: "visible",
        }),
      }}
    >
      {isMobile ? (
        boards.map((board) => {
          const percent =
            (100 * board.nbParticipants) / parseInt(board.nbPlace);
          const tooltipText = `Tableau rempli à ${Math.round(
            percent
          )}%. Il reste ${
            parseInt(board.nbPlace) - board.nbParticipants
          } places.`;

          return (
            <Card key={board.name} elevation={4}>
              <CardContent>
                <Typography variant="h6" fontWeight={600}>
                  Tableau {board.name}
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={500}
                  color="text.primary"
                >
                  {board.label}
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Stack spacing={1}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CalendarTodayIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      {new Date(board.date).toLocaleDateString("fr-FR", {
                        dateStyle: "full",
                      })}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccessTimeIcon fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Pointage jusqu'à {board.heureLimite}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <GroupIcon fontSize="small" />
                    <Typography variant="body2">
                      {board.nbParticipants} inscrit(s)
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EventSeatIcon fontSize="small" />
                    <Typography variant="body2">
                      {board.nbPlace} place(s)
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Tooltip title={tooltipText}>
                      <LinearProgress
                        variant="determinate"
                        value={percent}
                        sx={{
                          flexGrow: 1,
                          height: 8,
                          borderRadius: 5,
                          backgroundColor: "grey.200",
                          "& .MuiLinearProgress-bar": {
                            borderRadius: 5,
                            backgroundColor: theme.palette.primary.main,
                          },
                        }}
                      />
                    </Tooltip>
                    <Chip
                      label={`${board.nbParticipants}/${board.nbPlace}`}
                      size="small"
                      sx={{
                        fontWeight: 500,
                        bgcolor: "primary.light",
                        color: "primary.contrastText",
                      }}
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          );
        })
      ) : (
        <Paper>
          <TableContainer sx={{ overflow: "auto", maxHeight }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="left">#</TableCell>
                  <TableCell align="left">
                    <Typography
                      sx={{
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <ChecklistIcon fontSize="small" /> Nom
                    </Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Typography
                      sx={{
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <CalendarTodayIcon fontSize="small" /> Jour
                    </Typography>
                  </TableCell>
                  <TableCell align="left">
                    <Typography
                      sx={{
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <AccessTimeIcon fontSize="small" /> Fin pointage
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      sx={{
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "center",
                      }}
                    >
                      <GroupIcon fontSize="small" /> Inscrits
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      sx={{
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "center",
                      }}
                    >
                      <EventSeatIcon fontSize="small" /> Places
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontWeight: 700 }}>
                      Remplissage
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {boards.map((board: Tableau, index: number) => {
                  const percent =
                    (100 * board.nbParticipants) / parseInt(board.nbPlace);
                  const tooltipText = `Tableau rempli à ${Math.round(
                    percent
                  )}%. Il reste ${
                    parseInt(board.nbPlace) - board.nbParticipants
                  } places.`;

                  return (
                    <TableRow
                      key={board.name}
                      sx={{
                        backgroundColor:
                          index % 2 === 0
                            ? theme.palette.secondary.light + "33" // clair pour lignes paires
                            : theme.palette.primary.light + "22", // plus chaud pour impaires
                        "&:hover": {
                          backgroundColor: theme.palette.primary.light + "55",
                        },
                      }}
                    >
                      <TableCell align="left">
                        <Typography variant="body1" fontWeight={500}>
                          {board.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        <Typography
                          variant="body1"
                          fontWeight={500}
                          color="text.primary"
                        >
                          {board.label}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        <Typography
                          variant="body1"
                          fontWeight={400}
                          color="text.primary"
                        >
                          {new Date(board.date).toLocaleDateString("fr-FR", {
                            dateStyle: "full",
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        <Typography
                          variant="body1"
                          fontWeight={400}
                          color="text.secondary"
                        >
                          {board.heureLimite}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1" fontWeight={500}>
                          {board.nbParticipants}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1" fontWeight={500}>
                          {board.nbPlace}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={tooltipText}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 1,
                            }}
                          >
                            <LinearProgress
                              variant="determinate"
                              value={percent}
                              sx={{
                                width: 80,
                                height: 8,
                                borderRadius: 5,
                                backgroundColor: "grey.200",
                                "& .MuiLinearProgress-bar": {
                                  borderRadius: 5,
                                  backgroundColor: theme.palette.primary.main,
                                },
                              }}
                            />
                            <Chip
                              label={`${board.nbParticipants}/${board.nbPlace}`}
                              size="small"
                              sx={{
                                fontWeight: 500,
                                bgcolor: "primary.light",
                                color: "primary.contrastText",
                              }}
                            />
                          </Box>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

const TableRowsLoader: React.FC<{ rowsNum: number }> = ({ rowsNum }) => (
  <Box>
    {[...Array(rowsNum)].map((_, index) => (
      <Skeleton key={index} variant="rectangular" height={80} sx={{ mb: 2 }} />
    ))}
  </Box>
);

export default BoardList;
